/**
 * 智擎新界面 · 模块导航注册表（单一真相源 · 对齐 v2 设计稿 navDefs）。
 *
 * 侧栏顺序/图标/角标严格按 v2 .dc.html。`agent`(Agent 任务中心) 是侧栏项;
 * `mobile`(移动审批端) 不进侧栏(hideInSidebar) · 由顶栏「移动端预览」按钮进入。
 * `kind`：real=接真实后端 · static=高保真静态原型（假数据 · 带「演示数据」角标）。
 */
import {
  Blocks,
  Bot,
  Cable,
  CalendarClock,
  CircleDollarSign,
  CircleGauge,
  Cpu,
  DatabaseZap,
  DraftingCompass,
  Factory,
  FilePenLine,
  FolderKanban,
  Gauge,
  GitCompareArrows,
  Library,
  MessagesSquare,
  Settings,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Spline,
  Truck,
  Zap,
  type LucideIcon,
} from "lucide-react";

export type ModuleId =
  | "home"
  | "cockpit"
  | "quickquote"
  | "drawing"
  | "quote"
  | "reqs"
  | "projects"
  | "orders"
  | "mech"
  | "elec"
  | "plc"
  | "ecn"
  | "process"
  | "machines"
  | "dataHealth"
  | "factoryCalendar"
  | "cnc"
  | "verify"
  | "kb"
  | "agent"
  | "caps"
  | "connectors"
  | "admin"
  | "mobile";

export interface ModuleNavItem {
  id: ModuleId;
  /** 路由路径（/app 下） */
  path: string;
  label: string;
  icon: LucideIcon;
  /** real=接后端 · static=高保真静态原型 */
  kind: "real" | "static";
  /** 角标数字（可选） */
  badge?: number;
  /** 不在左侧栏渲染（由顶栏入口进入，如移动审批端） */
  hideInSidebar?: boolean;
  /** 侧栏标「待上线中」（尚未正式上线的模块 · 仅这些显标） */
  comingSoon?: boolean;
  /** 拼音/关键词，给 ⌘K 命令面板模糊匹配用 */
  kw?: string;
}

export const MODULES: ModuleNavItem[] = [
  {
    id: "home",
    path: "/app/home",
    label: "Agent 首页",
    icon: Sparkles,
    kind: "real",
    kw: "agent shouye home",
  },
  {
    id: "cockpit",
    path: "/app/cockpit",
    label: "智擎驾驶舱",
    icon: CircleGauge,
    kind: "real",
    kw: "zhiqing zhizao jiashicang cockpit dashboard shuju feilun jichuang yaoce",
  },
  {
    id: "quickquote",
    path: "/app/quickquote",
    label: "快速报价",
    icon: Gauge,
    kind: "real",
    kw: "kuaisu baojia quickquote gongshi",
  },
  {
    id: "drawing",
    path: "/app/drawing",
    label: "出图确认",
    icon: FilePenLine,
    kind: "real",
    kw: "chutu queren drawing gongchengtu tuzhi",
  },
  {
    id: "quote",
    path: "/app/quote",
    label: "投标报价",
    icon: CircleDollarSign,
    kind: "real",
    kw: "shangwu toubiao baojia quote",
  },
  {
    id: "reqs",
    path: "/app/reqs",
    label: "需求与技术交流",
    icon: MessagesSquare,
    kind: "real",
    kw: "xuqiu reqs",
  },
  {
    id: "projects",
    path: "/app/projects",
    label: "项目台账",
    icon: FolderKanban,
    kind: "real",
    kw: "xiangmu taizhang projects kehu changqu chanwu tupu",
  },
  {
    id: "orders",
    path: "/app/orders",
    label: "订单交付",
    icon: Truck,
    kind: "real",
    kw: "dingdan jiaofu jiaoqi fahuo wangong orders delivery chengjiao",
  },
  {
    id: "mech",
    path: "/app/mech",
    label: "机械设计",
    icon: DraftingCompass,
    kind: "static",
    kw: "jixie mech",
  },
  {
    id: "elec",
    path: "/app/elec",
    label: "电气设计",
    icon: Zap,
    kind: "static",
    comingSoon: true,
    kw: "dianqi elec",
  },
  {
    id: "plc",
    path: "/app/plc",
    label: "PLC 工程",
    icon: Cpu,
    kind: "static",
    comingSoon: true,
    kw: "plc gongcheng",
  },
  {
    id: "ecn",
    path: "/app/ecn",
    label: "ECN 变更中心",
    icon: GitCompareArrows,
    kind: "static",
    kw: "ecn biangeng",
  },
  {
    id: "process",
    path: "/app/process",
    label: "工艺与制造",
    icon: Factory,
    kind: "real",
    kw: "gongyi zhizao process",
  },
  {
    id: "machines",
    path: "/app/machines",
    label: "机床监控",
    icon: Cpu,
    kind: "real",
    kw: "设备 机床 遥测 O号 映射 shebei jichuang yaoce machines telemetry",
  },
  {
    id: "dataHealth",
    path: "/app/data-health",
    label: "数据接入与质量",
    icon: DatabaseZap,
    kind: "real",
    kw: "shuju jieru zhiliang data health caiji fugai wangguan daiban koujing",
  },
  {
    id: "factoryCalendar",
    path: "/app/factory-calendar",
    label: "班次设置",
    icon: CalendarClock,
    kind: "real",
    kw: "banci paiban xiuxi jihua gongshi oee shift calendar",
  },
  {
    id: "cnc",
    path: "/app/cnc",
    label: "CNC 闭环",
    icon: Spline,
    kind: "real",
    kw: "cnc bihuan",
  },
  {
    id: "verify",
    path: "/app/verify",
    label: "验证与评审",
    icon: ShieldCheck,
    kind: "real",
    kw: "yanzheng pingshen verify",
  },
  {
    id: "kb",
    path: "/app/kb",
    label: "工业知识库",
    icon: Library,
    kind: "static",
    kw: "zhishiku kb",
  },
  {
    id: "agent",
    path: "/app/agent",
    label: "Agent 任务中心",
    icon: Bot,
    kind: "real",
    kw: "agent renwu task zhongxin",
  },
  {
    id: "caps",
    path: "/app/caps",
    label: "能力中心",
    icon: Blocks,
    kind: "static",
    kw: "nengli capability skills plugins jineng chajian caps",
  },
  {
    id: "connectors",
    path: "/app/connectors",
    label: "数据连接器",
    icon: Cable,
    kind: "static",
    kw: "lianjieqi connectors mcp",
  },
  {
    id: "admin",
    path: "/app/admin",
    label: "系统管理",
    icon: Settings,
    kind: "real",
    kw: "xitong guanli admin",
  },
  {
    id: "mobile",
    path: "/app/mobile",
    label: "移动审批端",
    icon: Smartphone,
    kind: "static",
    hideInSidebar: true,
    kw: "yidong shenpi mobile",
  },
];

export const MODULE_BY_ID: Record<ModuleId, ModuleNavItem> = MODULES.reduce(
  (acc, m) => {
    acc[m.id] = m;
    return acc;
  },
  {} as Record<ModuleId, ModuleNavItem>,
);

/** Presentation ownership is independent of stable ModuleId and URL identities. */
export type NavigationGroupId =
  | "business"
  | "engineering"
  | "resources"
  | "knowledge"
  | "system";
export interface NavigationItem {
  id: string;
  group: NavigationGroupId | null;
  label: string;
  path: string;
  icon: LucideIcon;
  aliases: ModuleId[];
  paths: string[];
  keywords: string;
  restricted?: boolean;
}
export const NAVIGATION_GROUPS: { id: NavigationGroupId; label: string }[] = [
  { id: "business", label: "项目与商务" },
  { id: "engineering", label: "工程与制造" },
  { id: "resources", label: "设备与资源" },
  { id: "knowledge", label: "知识与能力" },
  { id: "system", label: "数据与系统" },
];
const item = (
  id: string,
  group: NavigationGroupId | null,
  label: string,
  path: string,
  icon: LucideIcon,
  aliases: ModuleId[],
  paths: string[] = [],
  keywords = "",
  restricted = false,
): NavigationItem => ({
  id,
  group,
  label,
  path,
  icon,
  aliases,
  paths: [path, ...paths],
  keywords,
  restricted,
});
export const NAVIGATION_ITEMS: NavigationItem[] = [
  item(
    "workspace",
    null,
    "AI 工作台",
    "/app/home",
    Sparkles,
    ["home", "agent"],
    ["/app/agent"],
    "ai gongzuotai",
  ),
  item(
    "overview",
    null,
    "经营总览",
    "/app/cockpit",
    CircleGauge,
    ["cockpit"],
    [],
    "jingying zonglan",
  ),
  item(
    "projects",
    "business",
    "项目档案",
    "/app/projects",
    FolderKanban,
    ["projects"],
    ["/admin/projects", "/admin/customers"],
    "xiangmu dangan",
  ),
  item(
    "requirements",
    "business",
    "需求与技术确认",
    "/app/reqs",
    MessagesSquare,
    ["reqs"],
    [],
    "xuqiu jishu queren",
  ),
  item(
    "quotes",
    "business",
    "报价工作台",
    "/app/quickquote",
    Gauge,
    ["quickquote", "quote"],
    ["/app/quote", "/cnc/quotes", "/cnc/quote-projects", "/cnc/line-quotes"],
    "baojia gongzuotai",
  ),
  item(
    "orders",
    "business",
    "订单与交付",
    "/app/orders",
    Truck,
    ["orders"],
    [],
    "dingdan jiaofu",
  ),
  item(
    "parts",
    "engineering",
    "零件与图纸",
    "/app/mech",
    DraftingCompass,
    ["mech", "drawing"],
    [
      "/app/drawing",
      "/admin/parts",
      "/cnc/generate-model",
      "/cnc/redesign",
      "/cnc/drawing",
      "/cnc/drawing-batch",
    ],
    "lingjian tuzhi",
  ),
  item(
    "nc",
    "engineering",
    "NC 编程与优化",
    "/app/process",
    Factory,
    ["process", "cnc"],
    [
      "/app/cnc",
      "/cnc/workbench",
      "/cnc/upload",
      "/cnc/generate",
      "/cnc/rules",
      "/cnc/programs",
    ],
    "nc biancheng youhua",
  ),
  item(
    "review",
    "engineering",
    "程序验证与评审",
    "/app/verify",
    ShieldCheck,
    ["verify"],
    ["/cnc/signoff", "/cnc/dashboard", "/cnc/audit"],
    "chengxu yanzheng pingshen",
  ),
  item(
    "ecn",
    "engineering",
    "工程变更（ECN）",
    "/app/ecn",
    GitCompareArrows,
    ["ecn"],
    [],
    "gongcheng biangeng",
  ),
  item(
    "machines",
    "resources",
    "机床与运行",
    "/app/machines",
    Cpu,
    ["machines"],
    [],
    "jichuang yunxing",
  ),
  item(
    "tools",
    "resources",
    "刀具台账",
    "/admin/tool-images",
    DraftingCompass,
    [],
    ["/admin/tools"],
    "daoju taizhang",
    true,
  ),
  item(
    "calendar",
    "resources",
    "班次与日历",
    "/app/factory-calendar",
    CalendarClock,
    ["factoryCalendar"],
    [],
    "banci rili",
  ),
  item(
    "kb",
    "knowledge",
    "工业知识库",
    "/app/kb",
    Library,
    ["kb"],
    [],
    "gongye zhishiku",
  ),
  item(
    "capabilities",
    "knowledge",
    "技能与工具",
    "/app/caps",
    Blocks,
    ["caps", "elec", "plc"],
    ["/app/elec", "/app/plc", "/admin/tenant-skills"],
    "jineng gongju 规划能力",
  ),
  item(
    "data",
    "system",
    "数据质量与接入状态",
    "/app/data-health",
    DatabaseZap,
    ["dataHealth"],
    [],
    "shuju zhiliang jieru",
  ),
  item(
    "connectors",
    "system",
    "系统连接器",
    "/app/connectors",
    Cable,
    ["connectors"],
    [],
    "xitong lianjieqi",
  ),
  item(
    "gateway",
    "system",
    "网关与采集配置",
    "/admin/edge-gateways",
    Cable,
    [],
    [],
    "wangguan caiji",
    true,
  ),
  item(
    "settings",
    "system",
    "系统设置",
    "/app/admin",
    Settings,
    ["admin"],
    ["/admin", "/cnc/billing"],
    "xitong shezhi",
  ),
];

/** Most specific path wins; query and fragment never affect ownership. */
export function resolveNavigationOwner(
  url: string,
): NavigationItem | undefined {
  const pathname = url.split(/[?#]/, 1)[0].replace(/\/$/, "") || "/";
  let result: NavigationItem | undefined;
  let length = -1;
  for (const entry of NAVIGATION_ITEMS)
    for (const prefix of entry.paths) {
      if (
        (pathname === prefix || pathname.startsWith(`${prefix}/`)) &&
        prefix.length > length
      ) {
        result = entry;
        length = prefix.length;
      }
    }
  return result;
}

/** Preserve an open conversation and its exact URL; restore only this user's last session. */
export function workspaceDestination(
  location: { pathname: string; search: string; hash: string },
  userId?: string,
): string {
  if (location.pathname === "/app/agent" || location.pathname === "/app/home")
    return location.pathname + location.search + location.hash;
  if (!userId) return "/app/home";
  try {
    if (localStorage.getItem(`app-agent:last:${userId}`)) return "/app/agent";
  } catch {
    /* storage unavailable */
  }
  return "/app/home";
}

/** A denied legacy resource/config page returns to an authorized reading entry. */
export function navigationReturnDestination(url: string): string {
  const owner = resolveNavigationOwner(url);
  if (owner?.id === "tools") return "/app/machines";
  if (owner?.id === "gateway") return "/app/data-health";
  return owner?.path ?? "/app/home";
}
